// src/components/fx/CrackerScene.jsx
//
// A cluster of natural-looking burning crackers, drawn live on <canvas>:
//   • Two big wheel chakkars (candy-striped rims, ornate faces) spinning
//     and throwing spark jets
//   • Flower pot (anar) with a golden fountain + smoke
//   • Red/gold striped cracker and purple floral cracker with lit fuses
//   • Round bomb with a lit fuse
//   • Sparkler with branching crackles
//
// Fills its (position:relative) parent; the crackers stand in the right
// part so text on the left stays readable. Pauses off-screen / hidden tab.
// Reduced-motion users get one still frame.

import React, { useEffect, useRef } from 'react';

const WARM = ['#fff8e1', '#ffe8a3', '#ffd166', '#ffb347', '#ffffff', '#ffc94d'];
const rnd  = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];

export default function CrackerScene({ className = '', full = false }) {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let w = 0, h = 0, raf = 0, visible = true, frame = 0, spin = 0;
    const sparks = [];
    const smoke  = [];
    const fuseTips = [];          // world positions of lit fuses, refreshed each draw

    const resize = () => {
      const r = cv.getBoundingClientRect();
      w = r.width; h = r.height;
      cv.width = Math.max(1, w * dpr); cv.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    /* ── Layout ─────────────────────────────────────────────── */
    const L = () => {
      const phone = w < 640;
      // full = the canvas box is only for the crackers (use all of it)
      const x0 = full ? w * 0.02 : w * (phone ? 0.50 : 0.48);
      const x1 = full ? w * 0.98 : w * (phone ? 0.97 : w < 1024 ? 1.0 : 0.97);
      const rw = x1 - x0;
      const X  = (f) => x0 + rw * f;
      const gy = h * 0.96;
      const u  = Math.min(h * 0.98, rw * (full ? 0.6 : phone ? 0.66 : 0.62));
      return {
        gy, u,
        wheelL: { x: X(0.15), y: gy - u * 0.42, r: u * 0.25 },
        wheelR: { x: X(0.87), y: gy - u * 0.38, r: u * 0.22 },
        stick:  { bx: X(0.60), by: gy, tx: X(0.70), ty: gy - u * 0.80 },
        anar:   { x: X(0.47), top: gy - u * 0.40, bw: u * 0.13, tw: u * 0.05 },
        cylA:   { x: X(0.27), y: gy, cw: u * 0.17, ch: u * 0.36, tilt: -0.22 },
        cylB:   { x: X(0.69), y: gy, cw: u * 0.16, ch: u * 0.31, tilt: 0.2 },
        bomb:   { x: X(0.85), y: gy - u * 0.11, r: u * 0.11 },
      };
    };

    const cap = () => sparks.length < 1500;
    const add = (o) => { if (cap()) sparks.push(o); };

    /* ── Emitters ───────────────────────────────────────────── */
    const emit = (l) => {
      const { u, anar, stick, wheelL, wheelR } = l;
      // flower pot fountain
      for (let i = 0; i < 12; i++) {
        const a = -Math.PI / 2 + rnd(-0.3, 0.3), s = u * rnd(0.016, 0.034);
        add({ x: anar.x + rnd(-2, 2), y: anar.top, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
          life: 0, max: rnd(40, 80), col: pick(WARM), lw: rnd(1, 1.9), g: 1, branch: Math.random() < 0.06 });
      }
      // sparkler
      for (let i = 0; i < 7; i++) {
        const a = rnd(0, Math.PI * 2), s = u * rnd(0.008, 0.026);
        add({ x: stick.tx, y: stick.ty, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
          life: 0, max: rnd(6, 15), col: pick(['#ffffff', '#fff6d5', '#ffe08a']), lw: rnd(.7, 1.2), g: .4,
          branch: Math.random() < 0.55 });
      }
      // wheel rim jets (tangential)
      [[wheelL, ['#ffd54a', '#fff3c4', '#ffb300']], [wheelR, ['#b8ff8a', '#eaffd0', '#7dff5a']]].forEach(([wh, cols], j) => {
        const dir = j ? -1 : 1;
        for (let k = 0; k < 2; k++) {
          const a = spin * dir + k * Math.PI;
          const px = wh.x + Math.cos(a) * wh.r, py = wh.y + Math.sin(a) * wh.r;
          const tx = -Math.sin(a) * dir, ty = Math.cos(a) * dir;
          for (let i = 0; i < 3; i++) {
            const s = u * rnd(0.012, 0.026);
            add({ x: px, y: py, vx: tx * s + rnd(-.3, .3), vy: ty * s + rnd(-.3, .3),
              life: 0, max: rnd(14, 28), col: pick(cols), lw: rnd(1, 1.5), g: .8, branch: false });
          }
        }
      });
      // lit fuses
      fuseTips.forEach(([fx, fy]) => {
        for (let i = 0; i < 2; i++) {
          const a = rnd(0, Math.PI * 2), s = u * rnd(0.004, 0.012);
          add({ x: fx, y: fy, vx: Math.cos(a) * s, vy: Math.sin(a) * s - u * .002,
            life: 0, max: rnd(5, 11), col: pick(['#fff', '#ffe08a', '#ffb347']), lw: .9, g: .3, branch: false });
        }
      });
      // smoke
      if (frame % 5 === 0 && smoke.length < 40) {
        smoke.push({ x: anar.x + rnd(-4, 4), y: anar.top - u * 0.05, vx: rnd(-.15, .25), vy: -u * rnd(.0025, .004),
          r: u * rnd(.03, .05), a: rnd(.08, .13) });
      }
    };

    const update = (l) => {
      const gr = l.u * 0.00055;
      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.px = p.x; p.py = p.y;
        p.vx *= 0.985; p.vy = p.vy * 0.985 + gr * p.g;
        p.x += p.vx; p.y += p.vy; p.life++;
        if (p.y > l.gy) { p.y = l.gy; p.vy *= -0.3; p.vx *= 0.6; p.life += 6; }
        if (p.life >= p.max) {
          if (p.branch) {
            const n = 2 + ((Math.random() * 2) | 0);
            for (let k = 0; k < n; k++) {
              const a = rnd(0, Math.PI * 2), s = l.u * rnd(.004, .012);
              add({ x: p.x, y: p.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 0,
                max: rnd(4, 9), col: '#fffbe6', lw: .8, g: .4, branch: false });
            }
          }
          sparks.splice(i, 1);
        }
      }
      for (let i = smoke.length - 1; i >= 0; i--) {
        const s = smoke[i];
        s.x += s.vx; s.y += s.vy; s.r *= 1.012; s.a *= 0.985;
        if (s.a < 0.01 || s.y < -s.r) smoke.splice(i, 1);
      }
      spin += 0.12;
    };

    /* ── Drawing helpers ────────────────────────────────────── */
    const glow = (x, y, r, stops) => {
      if (r <= 0) return;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      stops.forEach(([o, c]) => g.addColorStop(o, c));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    };
    const shade = (x, y, r) => {   // spherical/disc shading overlay
      const g = ctx.createRadialGradient(x - r * .35, y - r * .4, r * .05, x, y, r * 1.05);
      g.addColorStop(0, 'rgba(255,255,255,.35)'); g.addColorStop(.45, 'rgba(255,255,255,0)');
      g.addColorStop(1, 'rgba(0,0,0,.45)');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    };

    const drawWheel = ({ x, y, r }, rot, face) => {
      // drop shadow
      ctx.fillStyle = 'rgba(0,0,0,.35)';
      ctx.beginPath(); ctx.arc(x + r * .06, y + r * .08, r, 0, Math.PI * 2); ctx.fill();
      // candy-striped rim
      const N = 40, step = (Math.PI * 2) / N, stripe = ['#ffffff', '#e63946', '#ffffff', '#3a86ff'];
      ctx.lineWidth = r * 0.17;
      for (let k = 0; k < N; k++) {
        ctx.strokeStyle = stripe[k % 4];
        ctx.beginPath(); ctx.arc(x, y, r * 0.91, rot + k * step, rot + (k + 1) * step + 0.01); ctx.stroke();
      }
      // face
      const fg = ctx.createRadialGradient(x, y, r * .1, x, y, r * .82);
      fg.addColorStop(0, face[0]); fg.addColorStop(.6, face[1]); fg.addColorStop(1, face[2]);
      ctx.fillStyle = fg; ctx.beginPath(); ctx.arc(x, y, r * 0.82, 0, Math.PI * 2); ctx.fill();
      // ornament rings + dots (rotate with the wheel)
      ctx.strokeStyle = face[3]; ctx.lineWidth = Math.max(1, r * .035);
      [.72, .5, .3].forEach(f => { ctx.beginPath(); ctx.arc(x, y, r * f, 0, Math.PI * 2); ctx.stroke(); });
      ctx.fillStyle = face[3];
      for (let k = 0; k < 14; k++) {
        const a = rot + k * (Math.PI * 2 / 14);
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * r * .61, y + Math.sin(a) * r * .61, r * .04, 0, Math.PI * 2); ctx.fill();
      }
      // centre star
      ctx.fillStyle = face[4];
      ctx.beginPath();
      for (let k = 0; k < 16; k++) {
        const a = rot + k * Math.PI / 8, rr = k % 2 ? r * .1 : r * .25;
        const px = x + Math.cos(a) * rr, py = y + Math.sin(a) * rr;
        k ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
      }
      ctx.closePath(); ctx.fill();
      shade(x, y, r);
    };

    const drawCylinder = ({ x, y, cw, ch, tilt }, style) => {
      ctx.save();
      ctx.translate(x, y); ctx.rotate(tilt);
      const hw = cw / 2;
      // shadow
      ctx.fillStyle = 'rgba(0,0,0,.35)';
      ctx.beginPath(); ctx.ellipse(0, 0, hw * 1.2, cw * .16, 0, 0, Math.PI * 2); ctx.fill();
      // body
      ctx.save();
      ctx.beginPath(); ctx.rect(-hw, -ch, cw, ch); ctx.clip();
      ctx.fillStyle = style === 'stripe' ? '#d62828' : '#6a1b9a';
      ctx.fillRect(-hw, -ch, cw, ch);
      if (style === 'stripe') {
        ctx.fillStyle = '#f4c95d';
        for (let yy = -ch - cw; yy < cw; yy += cw * .55) {
          ctx.beginPath(); ctx.moveTo(-hw, yy); ctx.lineTo(hw, yy - cw * .5);
          ctx.lineTo(hw, yy - cw * .5 + cw * .2); ctx.lineTo(-hw, yy + cw * .2); ctx.closePath(); ctx.fill();
        }
      } else {
        ctx.fillStyle = '#f4c95d';
        for (let yy = -ch + cw * .25; yy < 0; yy += cw * .32) {
          for (let xx = -hw + cw * .2 + ((yy / (cw * .32)) % 2 ? cw * .15 : 0); xx < hw; xx += cw * .3) {
            for (let k = 0; k < 5; k++) {       // 5-petal flower
              const a = k * Math.PI * 2 / 5;
              ctx.beginPath(); ctx.arc(xx + Math.cos(a) * cw * .045, yy + Math.sin(a) * cw * .045, cw * .035, 0, Math.PI * 2); ctx.fill();
            }
          }
        }
      }
      // cylindrical shading
      const sg = ctx.createLinearGradient(-hw, 0, hw, 0);
      sg.addColorStop(0, 'rgba(0,0,0,.55)'); sg.addColorStop(.3, 'rgba(255,255,255,.22)');
      sg.addColorStop(.5, 'rgba(255,255,255,0)'); sg.addColorStop(1, 'rgba(0,0,0,.6)');
      ctx.fillStyle = sg; ctx.fillRect(-hw, -ch, cw, ch);
      ctx.restore();
      // gold rims
      ctx.fillStyle = '#e0b04a';
      ctx.fillRect(-hw, -ch, cw, cw * .08); ctx.fillRect(-hw, -cw * .08, cw, cw * .08);
      // top cap
      ctx.fillStyle = style === 'stripe' ? '#f4c95d' : '#9c4dcc';
      ctx.beginPath(); ctx.ellipse(0, -ch, hw, cw * .16, 0, 0, Math.PI * 2); ctx.fill();
      // fuse
      const fx = cw * .28, fy = -ch - cw * .5;
      ctx.strokeStyle = '#5d4037'; ctx.lineWidth = Math.max(1.2, cw * .05); ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(0, -ch); ctx.quadraticCurveTo(-cw * .1, -ch - cw * .3, fx, fy); ctx.stroke();
      ctx.restore();
      // tip in world space
      const c = Math.cos(tilt), s = Math.sin(tilt);
      return [x + fx * c - fy * s, y + fx * s + fy * c];
    };

    const drawBomb = ({ x, y, r }) => {
      ctx.fillStyle = 'rgba(0,0,0,.35)';
      ctx.beginPath(); ctx.ellipse(x, y + r * .95, r * 1.05, r * .22, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#c1121f';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      ctx.save(); ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.clip();
      ctx.strokeStyle = '#f4c95d'; ctx.lineWidth = r * .18;
      ctx.beginPath(); ctx.ellipse(x, y, r, r * .32, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(x, y, r * .32, r, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = '#f4c95d';
      for (let k = 0; k < 6; k++) {
        const a = k * Math.PI / 3 + .5;
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * r * .62, y + Math.sin(a) * r * .62, r * .06, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      shade(x, y, r);
      ctx.fillStyle = '#e0b04a'; ctx.fillRect(x - r * .18, y - r * 1.12, r * .36, r * .2);
      ctx.strokeStyle = '#5d4037'; ctx.lineWidth = Math.max(1.2, r * .08);
      const tx = x + r * .45, ty = y - r * 1.65;
      ctx.beginPath(); ctx.moveTo(x, y - r * 1.1); ctx.quadraticCurveTo(x + r * .05, y - r * 1.5, tx, ty); ctx.stroke();
      return [tx, ty];
    };

    const drawAnar = ({ anar, gy, u }, flick) => {
      const { x, top, bw, tw } = anar;
      ctx.fillStyle = 'rgba(0,0,0,.35)';
      ctx.beginPath(); ctx.ellipse(x, gy, bw * 1.3, u * 0.02, 0, 0, Math.PI * 2); ctx.fill();
      const cone = () => { ctx.beginPath(); ctx.moveTo(x - bw, gy); ctx.lineTo(x + bw, gy); ctx.lineTo(x + tw, top); ctx.lineTo(x - tw, top); ctx.closePath(); };
      const body = ctx.createLinearGradient(x - bw, 0, x + bw, 0);
      body.addColorStop(0, '#3d0508'); body.addColorStop(.28, '#a3121c');
      body.addColorStop(.45, '#e8424f'); body.addColorStop(.6, '#b01622'); body.addColorStop(1, '#2e0306');
      ctx.fillStyle = body; cone(); ctx.fill();
      const hw = (f) => bw - (bw - tw) * f, yy = (f) => gy - (gy - top) * f;
      const band = (f1, f2, c0, c1) => {
        const g = ctx.createLinearGradient(x - bw, 0, x + bw, 0);
        g.addColorStop(0, c0); g.addColorStop(.45, c1); g.addColorStop(1, c0);
        ctx.fillStyle = g; ctx.beginPath();
        ctx.moveTo(x - hw(f1), yy(f1)); ctx.lineTo(x + hw(f1), yy(f1)); ctx.lineTo(x + hw(f2), yy(f2)); ctx.lineTo(x - hw(f2), yy(f2));
        ctx.closePath(); ctx.fill();
      };
      band(.08, .22, '#0b2a6b', '#3a6fd8');                // blue star band
      ctx.fillStyle = '#ffd76a';
      for (let k = -2; k <= 2; k++) {
        const sx = x + k * bw * .32, sy = yy(.15), sr = u * .012;
        ctx.beginPath();
        for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5, rr = i % 2 ? sr * .45 : sr;
          i ? ctx.lineTo(sx + Math.cos(a) * rr, sy + Math.sin(a) * rr) : ctx.moveTo(sx + Math.cos(a) * rr, sy + Math.sin(a) * rr); }
        ctx.closePath(); ctx.fill();
      }
      band(.26, .32, '#5c4300', '#ffd76a');
      band(.66, .74, '#5c4300', '#ffd76a');
      ctx.fillStyle = '#6d4c41';
      ctx.beginPath(); ctx.ellipse(x, top, tw, u * 0.013, 0, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = 'lighter';
      const lit = ctx.createLinearGradient(0, top, 0, top + (gy - top) * .6);
      lit.addColorStop(0, `rgba(255,190,90,${.35 * flick})`); lit.addColorStop(1, 'rgba(255,190,90,0)');
      ctx.fillStyle = lit; cone(); ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    };

    const drawStick = ({ stick, u }) => {
      const { bx, by, tx, ty } = stick;
      const lerp = (f) => [bx + (tx - bx) * f, by + (ty - by) * f];
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#8a8f98'; ctx.lineWidth = Math.max(1.2, u * .008);
      ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(tx, ty); ctx.stroke();
      ctx.strokeStyle = '#4a4a4f'; ctx.lineWidth = Math.max(2.5, u * .02);
      ctx.beginPath(); ctx.moveTo(...lerp(.35)); ctx.lineTo(...lerp(.86)); ctx.stroke();
      ctx.strokeStyle = '#ff7a1a';
      ctx.beginPath(); ctx.moveTo(...lerp(.86)); ctx.lineTo(...lerp(.93)); ctx.stroke();
    };

    /* ── Frame ──────────────────────────────────────────────── */
    const draw = (l) => {
      ctx.clearRect(0, 0, w, h);
      const flick = 0.8 + Math.random() * 0.4;
      const { anar, stick, wheelL, wheelR, gy, u } = l;

      // warm ground light
      ctx.globalCompositeOperation = 'lighter';
      glow(anar.x, gy, u * .8 * flick, [[0, 'rgba(255,150,50,.5)'], [1, 'rgba(255,150,50,0)']]);
      ctx.globalCompositeOperation = 'source-over';

      smoke.forEach(s => {
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
        g.addColorStop(0, `rgba(235,225,215,${s.a})`); g.addColorStop(1, 'rgba(235,225,215,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
      });

      // back row
      drawWheel(wheelL, spin, ['#fff1b8', '#f4a300', '#b35900', '#8f1016', '#d62828']);
      drawWheel(wheelR, -spin, ['#f3c4ff', '#9c27b0', '#4a0072', '#f4c95d', '#ffd60a']);
      drawStick(l);
      // front row
      fuseTips.length = 0;
      drawAnar(l, flick);
      fuseTips.push(drawCylinder(l.cylA, 'stripe'));
      fuseTips.push(drawCylinder(l.cylB, 'flower'));
      fuseTips.push(drawBomb(l.bomb));

      // sparks as streaks
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      for (const p of sparks) {
        const k = 1 - p.life / p.max;
        ctx.globalAlpha = Math.max(0, Math.min(1, k * 1.3));
        ctx.strokeStyle = p.col; ctx.lineWidth = p.lw;
        const sx = p.px ?? p.x - p.vx, sy = p.py ?? p.y - p.vy;
        ctx.beginPath(); ctx.moveTo(sx - (p.x - sx) * 1.2, sy - (p.y - sy) * 1.2); ctx.lineTo(p.x, p.y); ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // hot glows
      glow(anar.x, anar.top, u * .1 * flick, [[0, 'rgba(255,255,255,.95)'], [.3, 'rgba(255,220,120,.7)'], [1, 'rgba(255,150,40,0)']]);
      glow(stick.tx, stick.ty, u * .08 * flick, [[0, 'rgba(255,255,255,1)'], [.35, 'rgba(255,240,190,.6)'], [1, 'rgba(255,200,120,0)']]);
      fuseTips.forEach(([fx, fy]) => glow(fx, fy, u * .045 * flick, [[0, '#ffffff'], [.35, 'rgba(255,210,120,.8)'], [1, 'rgba(255,150,40,0)']]));
      [[wheelL, 1, 'rgba(255,214,80,.8)'], [wheelR, -1, 'rgba(160,255,120,.8)']].forEach(([wh, d, c]) => {
        for (let k = 0; k < 2; k++) {
          const a = spin * d + k * Math.PI;
          glow(wh.x + Math.cos(a) * wh.r, wh.y + Math.sin(a) * wh.r, u * .06, [[0, '#ffffff'], [.35, c], [1, 'rgba(0,0,0,0)']]);
        }
      });
      ctx.globalCompositeOperation = 'source-over';
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!visible || !w) return;
      const l = L();
      frame++; emit(l); update(l); draw(l);
    };

    const still = () => {
      if (!w) return;
      const l = L();
      draw(l);                                   // sets fuse tips
      for (let i = 0; i < 90; i++) { frame++; emit(l); update(l); }
      draw(l);
    };

    resize();
    const ro = new ResizeObserver(() => { resize(); if (reduce) still(); });
    ro.observe(cv);
    if (reduce) { still(); return () => ro.disconnect(); }

    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting && !document.hidden; });
    io.observe(cv);
    const onVis = () => { visible = !document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [full]);

  return <canvas ref={ref} className={`cs-canvas ${className}`} role="img"
    aria-label="Wheel chakkars, flower pot, crackers, bomb and sparkler burning" />;
}
